/**
 * Copyright (c) SAGE3 Development Team 2026. All Rights Reserved
 * University of Hawaii, University of Illinois Chicago, Virginia Tech
 *
 * Distributed under the terms of the SAGE3 License.  The full license is in
 * the file LICENSE, distributed as part of this software.
 *
 * Created by Pak Kwan 2026-07-29: PresentationController for Phase 2
 * Step-through delivery controller with animated viewport transitions
 */

import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import {
  Box,
  HStack,
  VStack,
  Button,
  IconButton,
  Text,
  Badge,
  useColorModeValue,
  Tooltip,
  useToast,
  Switch,
  FormControl,
  FormLabel,
} from '@chakra-ui/react';
import {
  MdPlayArrow,
  MdStop,
  MdNavigateBefore,
  MdNavigateNext,
  MdClose,
} from 'react-icons/md';

import { useThrottleApps, useLinkStore, useUIStore, useHexColor, useAnimatedViewport, useUser, usePresenceStore } from '@sage3/frontend';
import { getNarrativePath } from '@sage3/applications/apps';
import { App } from '@sage3/applications/schema';

// Phase 2B: the controller exposes these on window so NarrativePathView can start
// presentations / query state without a shared store. Declared globally so both
// the producer (this file) and the consumer (NarrativePathView) are typed.
declare global {
  interface Window {
    __startPresentation?: (startAppId?: string, pathIndex?: number, stepIndex?: number) => void;
    __isPresentationActive?: boolean;
  }
}

type PresentationControllerProps = {
  boardId: string;
};

export function PresentationController({ boardId }: PresentationControllerProps) {
  const apps = useThrottleApps(250);
  const links = useLinkStore((s) => s.links);
  const toast = useToast();
  const { animateTo } = useAnimatedViewport();
  const { user } = useUser();
  const setPresentationMode = useUIStore((s) => s.setPresentationMode);
  const selectedAppId = useUIStore((s) => s.selectedAppId);
  const updatePresence = usePresenceStore((s) => s.update);
  const presences = usePresenceStore((s) => s.presences);

  // Presentation state
  const [isPresentationMode, setIsPresentationMode] = useState<boolean>(false);
  const [narrativePath, setNarrativePath] = useState<string[]>([]);
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [revealedApps, setRevealedApps] = useState<string[]>([]); // Phase 3: Progressive reveal

  // Step synchronization mode: 'viewport' (default, current behavior) or 'step' (synchronized)
  const [syncMode, setSyncMode] = useState<'viewport' | 'step'>('viewport');

  // Match View mode: false (default, context view) or true (matched view)
  const [matchView, setMatchView] = useState<boolean>(false);

  // Return-to-Path Navigation state (Phase 2B)
  type DetourEntry = {
    pathIndex: number;
    stepIndex: number;
    reason: 'qa' | 'elaboration';
  };
  const [mainPath, setMainPath] = useState<{ pathIndex: number; currentStep: number } | null>(null);
  const [detourStack, setDetourStack] = useState<DetourEntry[]>([]);
  const [currentPathIndex, setCurrentPathIndex] = useState<number>(0); // Track which path we're currently viewing

  // Colors
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const tealColor = useHexColor('teal');

  // Calculate viewport target for an app (with padding)
  const calculateViewportTarget = useCallback((app: App) => {
    const { position, size } = app.data;

    // Center point of the app
    const targetX = position.x + size.width / 2;
    const targetY = position.y + size.height / 2;

    // Calculate scale to fit app (with 20% padding)
    const padding = 1.2;
    const scaleX = window.innerWidth / (size.width * padding);
    const scaleY = window.innerHeight / (size.height * padding);
    let targetScale = Math.min(scaleX, scaleY, 6); // Max zoom is 6 (from ui.ts)

    // Match View: If wall client and presenter has matchView enabled, adjust zoom
    if (user?.data.userType === 'wall') {
      // Find presenter's matchView setting
      const presenterPresence = presences.find((p) =>
        p.data.presentationMode === true
      );
      const shouldMatch = presenterPresence?.data.matchView;

      if (shouldMatch) {
        // Calculate zoom multiplier to match laptop view (1920×1080)
        const laptopWidth = 1920;
        const laptopHeight = 1080;
        const multiplier = Math.min(
          window.innerWidth / laptopWidth,
          window.innerHeight / laptopHeight
        );
        targetScale = targetScale * multiplier;
      }
    }

    // Convert to board position (center the target point on screen)
    const bx = -targetX + window.innerWidth / targetScale / 2;
    const by = -targetY + window.innerHeight / targetScale / 2;

    return { x: bx, y: by, scale: targetScale };
  }, [user, presences]);

  // Pre-calculate all viewport targets for the current path (performance optimization)
  // This eliminates calculation overhead when navigating, especially for wall clients
  const viewportTargets = useMemo(() => {
    const targets = new Map<number, { x: number; y: number; scale: number }>();
    narrativePath.forEach((appId, index) => {
      const app = apps.find((a) => a._id === appId);
      if (app) {
        targets.set(index, calculateViewportTarget(app));
      }
    });
    return targets;
  }, [narrativePath, apps, calculateViewportTarget]);

  // Focus on the app at the given step index (with animation)
  const focusOnApp = useCallback((stepIndex: number) => {
    if (stepIndex < 0 || stepIndex >= narrativePath.length) return;

    const appId = narrativePath[stepIndex];
    const currentApp = apps.find((a) => a._id === appId);

    // Use pre-calculated target for faster navigation (especially for wall clients)
    const target = viewportTargets.get(stepIndex);

    if (target) {
      animateTo(target.x, target.y, target.scale, { duration: 150 });
      setCurrentStep(stepIndex);

      // Phase 3: Progressive reveal - ONLY the presenter controls reveal state
      // Wall clients following in Step Sync mode should NOT update revealedApps
      // (they read it from presenter's presence instead)
      if (isPresentationMode) {
        // Phase 4: If this is a Frame, also reveal all apps inside it (Frame-as-slides support)
        setRevealedApps((prev) => {
          const toReveal: string[] = [appId];

          // If current app is a Frame, find and reveal all apps inside
          if (currentApp && currentApp.data.type === 'Frame') {
            const frame = currentApp.data;
            const appsInside = apps.filter((otherApp) => {
              if (otherApp._id === appId) return false; // Skip the Frame itself
              const appCenter = {
                x: otherApp.data.position.x + otherApp.data.size.width / 2,
                y: otherApp.data.position.y + otherApp.data.size.height / 2,
              };
              return (
                appCenter.x >= frame.position.x &&
                appCenter.x <= frame.position.x + frame.size.width &&
                appCenter.y >= frame.position.y &&
                appCenter.y <= frame.position.y + frame.size.height
              );
            });
            toReveal.push(...appsInside.map((a) => a._id));
          }

          // Add new apps to revealed list
          const newApps = toReveal.filter((id) => !prev.includes(id));
          if (newApps.length > 0) {
            const updated = [...prev, ...newApps];
            // Broadcast to other clients via presence
            if (user) {
              updatePresence(user._id, { revealedApps: updated });
            }
            return updated;
          }
          return prev;
        });
      }

      // Broadcast step change when in step sync mode (for synchronized wall navigation)
      if (syncMode === 'step' && user) {
        updatePresence(user._id, {
          presentationStep: stepIndex,
          presentationPath: narrativePath,
        });
      }
    } else {
      toast({
        title: 'App Not Found',
        description: `Could not find app at step ${stepIndex + 1}`,
        status: 'error',
        duration: 2000,
        isClosable: true,
        position: 'top',
      });
    }
  }, [narrativePath, viewportTargets, animateTo, toast, user, updatePresence, syncMode, isPresentationMode, apps]);

  // Start presentation: initialize path and focus on first app
  // Phase 2B: Now supports both initial start (linear mode) and mid-presentation jumps (detour mode)
  const handleStartPresentation = useCallback((startAppId?: string, pathIndex?: number, stepIndex?: number) => {
    // If no startAppId provided, find the first head in narrative links
    const boardLinks = links.filter((l) => l.data.boardId === boardId);
    const narrativeLinks = boardLinks.filter((l) => l.data.type === 'narrative_order');

    if (narrativeLinks.length === 0) {
      toast({
        title: 'No Narrative Path',
        description: 'Create a narrative path before starting presentation.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });
      return;
    }

    // Find a head (app with outgoing but no incoming narrative link)
    const appsWithIncoming = new Set(narrativeLinks.map((l) => l.data.targetAppId));
    const appsWithOutgoing = new Set(narrativeLinks.map((l) => l.data.sourceAppId));
    const heads = Array.from(appsWithOutgoing).filter((id) => !appsWithIncoming.has(id));

    const pathStartId = startAppId || heads[0];

    if (!pathStartId) {
      toast({
        title: 'Invalid Path',
        description: 'Could not find a starting point for the narrative path.',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });
      return;
    }

    // Get the full narrative path
    const path = getNarrativePath(pathStartId, boardLinks);

    if (path.length === 0) {
      toast({
        title: 'Empty Path',
        description: 'The narrative path is empty.',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });
      return;
    }

    // Validate path integrity: all apps must exist
    const missingApps = path.filter((appId) => !apps.find((a) => a._id === appId));
    if (missingApps.length > 0) {
      toast({
        title: 'Broken Path',
        description: `Path contains ${missingApps.length} missing app(s). Fix the path before presenting.`,
        status: 'error',
        duration: 4000,
        isClosable: true,
        position: 'top',
      });
      return;
    }

    // Determine which step to start at
    const targetStep = stepIndex !== undefined ? stepIndex : 0;

    // PHASE 2B: Detect if this is a DETOUR (mid-presentation jump) or INITIAL START
    const isDetour = isPresentationMode && mainPath !== null;

    if (isDetour) {
      // Push current location to detour stack
      setDetourStack((prev) => [
        ...prev,
        {
          pathIndex: currentPathIndex,
          stepIndex: currentStep,
          reason: 'qa', // Default to Q&A (could be enhanced with user choice)
        },
      ]);

      // Phase 3: Reveal the detour target app so it's not dimmed
      // Phase 4: If target is a Frame, also reveal apps inside (Frame-as-slides support)
      setRevealedApps((prev) => {
        const targetAppId = path[targetStep];
        const targetApp = apps.find((a) => a._id === targetAppId);
        const toReveal: string[] = [targetAppId];

        if (targetApp && targetApp.data.type === 'Frame') {
          const frame = targetApp.data;
          const appsInside = apps.filter((otherApp) => {
            if (otherApp._id === targetAppId) return false;
            const appCenter = {
              x: otherApp.data.position.x + otherApp.data.size.width / 2,
              y: otherApp.data.position.y + otherApp.data.size.height / 2,
            };
            return (
              appCenter.x >= frame.position.x &&
              appCenter.x <= frame.position.x + frame.size.width &&
              appCenter.y >= frame.position.y &&
              appCenter.y <= frame.position.y + frame.size.height
            );
          });
          toReveal.push(...appsInside.map((a) => a._id));
        }

        const newApps = toReveal.filter((id) => !prev.includes(id));
        if (newApps.length > 0) {
          const updated = [...prev, ...newApps];
          // Broadcast updated reveal state
          if (user) {
            updatePresence(user._id, { revealedApps: updated });
          }
          return updated;
        }
        return prev;
      });

      toast({
        title: 'Jumped to Content',
        description: `You can resume to Step ${currentStep + 1} later`,
        status: 'info',
        duration: 2000,
        isClosable: true,
        position: 'top',
      });
    } else {
      // Initial start: set up main path
      setMainPath({
        pathIndex: pathIndex !== undefined ? pathIndex : 0,
        currentStep: targetStep,
      });
      setIsPresentationMode(true);
      setPresentationMode(true); // Set global presentation mode flag (for Frame invisibility, etc.)

      // Phase 3: Initialize reveal state with first app
      // Phase 4: If first app is a Frame, also reveal apps inside (Frame-as-slides support)
      const firstAppId = path[targetStep];
      const firstApp = apps.find((a) => a._id === firstAppId);
      const initialRevealed: string[] = [firstAppId];

      if (firstApp && firstApp.data.type === 'Frame') {
        const frame = firstApp.data;
        const appsInside = apps.filter((otherApp) => {
          if (otherApp._id === firstAppId) return false;
          const appCenter = {
            x: otherApp.data.position.x + otherApp.data.size.width / 2,
            y: otherApp.data.position.y + otherApp.data.size.height / 2,
          };
          return (
            appCenter.x >= frame.position.x &&
            appCenter.x <= frame.position.x + frame.size.width &&
            appCenter.y >= frame.position.y &&
            appCenter.y <= frame.position.y + frame.size.height
          );
        });
        initialRevealed.push(...appsInside.map((a) => a._id));
      }

      setRevealedApps(initialRevealed);

      // Broadcast presentation mode and initial reveal state via presence (syncs to all clients).
      // presentationPath is broadcast in BOTH sync modes so that progressive-reveal dimming
      // (AppWindow's hasActivePath check) works in the default viewport mode, not only step-sync.
      // Phase 4B: Also broadcast anchored apps for sidebar
      if (user) {
        const anchoredIds = apps.filter((a) => a.data.anchor === true).map((a) => a._id);
        console.log("🚀 Starting presentation - broadcasting anchoredAppIds:", anchoredIds);
        updatePresence(user._id, {
          presentationMode: true,
          revealedApps: initialRevealed,
          presentationPath: path,
          matchView: matchView,
          anchoredAppIds: anchoredIds,
        });
      }

      toast({
        title: 'Presentation Started',
        description: `${path.length} steps in this presentation`,
        status: 'success',
        duration: 2000,
        isClosable: true,
        position: 'top',
      });
    }

    // Update current path and step
    setNarrativePath(path);
    setCurrentStep(targetStep);
    setCurrentPathIndex(pathIndex !== undefined ? pathIndex : 0);

    // Focus on target app with animation
    const targetApp = apps.find((a) => a._id === path[targetStep]);
    if (targetApp) {
      const target = calculateViewportTarget(targetApp);
      animateTo(target.x, target.y, target.scale, { duration: 150 });
    }
  }, [isPresentationMode, mainPath, currentPathIndex, currentStep, user, updatePresence, boardId, links, apps, calculateViewportTarget, animateTo, toast, setPresentationMode, matchView]);

  // Jump to standalone app (Phase 2B: Q&A content not in any narrative path)
  const handleJumpToStandaloneApp = useCallback((appId: string) => {
    if (!isPresentationMode || !mainPath) return;

    const targetApp = apps.find((a) => a._id === appId);
    if (!targetApp) {
      toast({
        title: 'App Not Found',
        description: 'Could not find the app you clicked.',
        status: 'error',
        duration: 2000,
        isClosable: true,
        position: 'top',
      });
      return;
    }

    // Push current location to detour stack
    setDetourStack((prev) => [
      ...prev,
      {
        pathIndex: currentPathIndex,
        stepIndex: currentStep,
        reason: 'qa',
      },
    ]);

    // Phase 3: Reveal the standalone app
    setRevealedApps((prev) => {
      if (!prev.includes(appId)) {
        const updated = [...prev, appId];
        if (user) {
          updatePresence(user._id, { revealedApps: updated });
        }
        return updated;
      }
      return prev;
    });

    // Create a temporary single-app "path" for the detour
    setNarrativePath([appId]);
    setCurrentStep(0);

    // Animate to the standalone app
    const target = calculateViewportTarget(targetApp);
    animateTo(target.x, target.y, target.scale, { duration: 150 });

    toast({
      title: 'Jumped to Q&A Content',
      description: 'Press R or click Resume to return',
      status: 'info',
      duration: 2000,
      isClosable: true,
      position: 'top',
    });
  }, [isPresentationMode, mainPath, currentPathIndex, currentStep, apps, user, updatePresence, calculateViewportTarget, animateTo, toast]);

  // Resume to main path (Phase 2B: Return-to-Path Navigation)
  const handleResume = useCallback(() => {
    if (detourStack.length === 0 || !mainPath) {
      toast({
        title: 'Nothing to Resume',
        description: 'No detour to return from.',
        status: 'warning',
        duration: 2000,
        isClosable: true,
        position: 'top',
      });
      return;
    }

    // Pop the most recent detour from the stack
    const lastDetour = detourStack[detourStack.length - 1];
    setDetourStack((prev) => prev.slice(0, -1));

    // Get all paths to find the correct one
    const boardLinks = links.filter((l) => l.data.boardId === boardId);
    const narrativeLinks = boardLinks.filter((l) => l.data.type === 'narrative_order');

    // Find all heads
    const appsWithIncoming = new Set(narrativeLinks.map((l) => l.data.targetAppId));
    const appsWithOutgoing = new Set(narrativeLinks.map((l) => l.data.sourceAppId));
    const heads = Array.from(appsWithOutgoing).filter((id) => !appsWithIncoming.has(id));

    // Get the path we're resuming to
    const resumePathHeadId = heads[lastDetour.pathIndex];
    if (!resumePathHeadId) {
      toast({
        title: 'Cannot Resume',
        description: 'The path you were on no longer exists.',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });
      return;
    }

    const resumePath = getNarrativePath(resumePathHeadId, boardLinks);
    const resumeAppId = resumePath[lastDetour.stepIndex];

    // Check if the resume app still exists
    const resumeApp = apps.find((a) => a._id === resumeAppId);
    if (!resumeApp) {
      toast({
        title: 'Cannot Resume',
        description: 'The app you were viewing has been deleted.',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });
      return;
    }

    // Restore the main path state
    setNarrativePath(resumePath);
    setCurrentStep(lastDetour.stepIndex);
    setCurrentPathIndex(lastDetour.pathIndex);

    // If this was the last detour, restore main path reference
    if (detourStack.length === 1) {
      // Back to main path mode
      toast({
        title: 'Resumed Main Path',
        description: `Back to Step ${lastDetour.stepIndex + 1} of ${resumePath.length}`,
        status: 'success',
        duration: 2000,
        isClosable: true,
        position: 'top',
      });
    } else {
      toast({
        title: 'Resumed Previous Location',
        description: `Back to Step ${lastDetour.stepIndex + 1}`,
        status: 'info',
        duration: 2000,
        isClosable: true,
        position: 'top',
      });
    }

    // Animate back to the resume location
    const target = calculateViewportTarget(resumeApp);
    animateTo(target.x, target.y, target.scale, { duration: 150 });
  }, [detourStack, mainPath, links, boardId, apps, calculateViewportTarget, animateTo, toast]);

  // Exit presentation
  const handleExitPresentation = useCallback(() => {
    setIsPresentationMode(false);
    setNarrativePath([]);
    setCurrentStep(0);
    setRevealedApps([]); // Phase 3: Clear reveal state
    setPresentationMode(false); // Clear global presentation mode flag

    // Phase 2B: Clear navigation stack
    setMainPath(null);
    setDetourStack([]);
    setCurrentPathIndex(0);

    // Reset sync mode to default
    setSyncMode('viewport');

    // Broadcast presentation mode off and clear all presentation state via presence
    if (user) {
      updatePresence(user._id, {
        presentationMode: false,
        revealedApps: [],
        presentationStep: -1,
        presentationPath: [],
        matchView: false,
        anchoredAppIds: [],
      });
    }

    toast({
      title: 'Presentation Ended',
      status: 'info',
      duration: 1500,
      isClosable: true,
      position: 'top',
    });
  }, [user, updatePresence, setPresentationMode, toast]);

  // Navigate to next step
  const handleNext = useCallback(() => {
    if (currentStep < narrativePath.length - 1) {
      focusOnApp(currentStep + 1);
    }
  }, [currentStep, narrativePath.length, focusOnApp]);

  // Navigate to previous step
  const handlePrevious = useCallback(() => {
    if (currentStep > 0) {
      focusOnApp(currentStep - 1);
    }
  }, [currentStep, focusOnApp]);

  // Wall clients: Listen for presenter's step changes when in step sync mode
  useEffect(() => {
    // Only wall clients should follow step changes
    if (user?.data.userType !== 'wall') return;

    // Find who we're following (should be the presenter)
    const myPresence = presences.find((p) => p._id === user._id);
    const followingUserId = myPresence?.data.following;
    if (!followingUserId) return;

    const presenterPresence = presences.find((p) => p._id === followingUserId);
    if (!presenterPresence) return;

    // Check if the presenter we're following is in presentation mode
    // (not our local isPresentationMode, which is only set when we start a presentation locally)
    if (!presenterPresence.data.presentationMode) return;

    const step = presenterPresence.data.presentationStep;
    const path = presenterPresence.data.presentationPath;

    // Only react if presenter has step sync enabled (step >= 0) and path is valid
    if (step >= 0 && path && path.length > 0) {
      // Update our local path if different (presenter might have switched paths)
      if (JSON.stringify(path) !== JSON.stringify(narrativePath)) {
        setNarrativePath(path);
      }

      // Navigate to the presenter's current step if different
      if (step !== currentStep) {
        focusOnApp(step);
      }
    }
  }, [
    user?.data.userType,
    user?._id,
    presences,
    currentStep,
    narrativePath,
    focusOnApp,
  ]);

  // Keyboard shortcuts
  useEffect(() => {
    if (!isPresentationMode) return;

    const handleKeyPress = (e: KeyboardEvent) => {
      // Arrow keys for navigation
      if (e.key === 'ArrowRight' || e.key === ' ') {
        e.preventDefault();
        handleNext();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        handlePrevious();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        handleExitPresentation();
      } else if (e.key === 'r' || e.key === 'R') {
        // Phase 2B: 'R' key to resume
        if (detourStack.length > 0) {
          e.preventDefault();
          handleResume();
        }
      } else if (e.key === 'S' && e.shiftKey) {
        // Shift+S: Toggle step synchronization mode
        e.preventDefault();
        const newMode = syncMode === 'step' ? 'viewport' : 'step';
        setSyncMode(newMode);

        // Clear step broadcast when switching back to viewport mode
        // NOTE: Keep presentationPath set so progressive reveal continues to work
        if (newMode === 'viewport' && user) {
          updatePresence(user._id, { presentationStep: -1 });
        }

        toast({
          title: 'Sync Mode Changed',
          description: newMode === 'step'
            ? '✓ Step Sync: Wall navigates simultaneously'
            : '○ Viewport Follow: Wall follows viewport (default)',
          status: 'info',
          duration: 2000,
          isClosable: true,
          position: 'top',
        });
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [isPresentationMode, handleNext, handlePrevious, handleExitPresentation, handleResume, detourStack.length, syncMode, user, updatePresence, toast]);

  // Phase 2B: Watch for app clicks during presentation and trigger detours
  useEffect(() => {
    if (!isPresentationMode || !selectedAppId || !mainPath) return;

    // Check if the clicked app is outside the current narrative path
    if (!narrativePath.includes(selectedAppId)) {
      // Find if this app is part of ANY narrative path (for proper path discovery)
      const boardLinks = links.filter((l) => l.data.boardId === boardId);
      const narrativeLinks = boardLinks.filter((l) => l.data.type === 'narrative_order');

      // Get all paths to see if this app is in another path
      const appsWithIncoming = new Set(narrativeLinks.map((l) => l.data.targetAppId));
      const appsWithOutgoing = new Set(narrativeLinks.map((l) => l.data.sourceAppId));
      const heads = Array.from(appsWithOutgoing).filter((id) => !appsWithIncoming.has(id));

      let foundInPath = false;
      for (const headId of heads) {
        const path = getNarrativePath(headId, boardLinks);
        if (path.includes(selectedAppId)) {
          foundInPath = true;
          // Find which path index this is (need to enumerate all paths)
          const allHeads = Array.from(appsWithOutgoing).filter((id) => !appsWithIncoming.has(id));
          const pathIndex = allHeads.indexOf(headId);
          const stepIndex = path.indexOf(selectedAppId);
          handleStartPresentation(headId, pathIndex, stepIndex);
          return;
        }
      }

      // App is not in any narrative path - it's standalone Q&A content
      if (!foundInPath) {
        handleJumpToStandaloneApp(selectedAppId);
      }
    }
  }, [selectedAppId, isPresentationMode, narrativePath, mainPath, links, boardId, handleStartPresentation, handleJumpToStandaloneApp]);

  // Expose start function globally so NarrativePathView can call it
  // Phase 2B: Also expose current presentation state for mid-presentation jumps
  useEffect(() => {
    window.__startPresentation = handleStartPresentation;
    window.__isPresentationActive = isPresentationMode;
    return () => {
      delete window.__startPresentation;
      delete window.__isPresentationActive;
    };
  }, [handleStartPresentation, isPresentationMode]);

  // Don't render controller UI if:
  // 1. Not in presentation mode, OR
  // 2. This is a wall client (presenter-view requirement - Phase 2)
  if (!isPresentationMode || user?.data.userType === 'wall') {
    return null;
  }

  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === narrativePath.length - 1;
  const currentApp = apps.find((a) => a._id === narrativePath[currentStep]);

  // Phase 2B: Detour state
  const isInDetour = detourStack.length > 0;
  const canResume = isInDetour && mainPath !== null;

  return (
    <Box
      position="fixed"
      bottom="8"
      left="50%"
      transform="translateX(-50%)"
      zIndex={10000}
      bg={bgColor}
      borderRadius="lg"
      borderWidth="2px"
      borderColor={borderColor}
      shadow="2xl"
      px={6}
      py={4}
    >
      <VStack spacing={3}>
        {/* Step Sync Toggle */}
        <FormControl display="flex" alignItems="center" justifyContent="center">
          <FormLabel htmlFor="step-sync" mb="0" fontSize="sm" mr={2}>
            Step Sync
          </FormLabel>
          <Tooltip
            label={
              syncMode === 'step'
                ? 'Wall navigates simultaneously (Shift+S to toggle)'
                : 'Wall follows viewport (default) (Shift+S to toggle)'
            }
            placement="top"
            hasArrow
          >
            <Switch
              id="step-sync"
              size="sm"
              colorScheme="teal"
              isChecked={syncMode === 'step'}
              onChange={(e) => {
                const newMode = e.target.checked ? 'step' : 'viewport';
                setSyncMode(newMode);

                // Clear step broadcast when switching back to viewport mode
                // NOTE: Keep presentationPath set so progressive reveal continues to work
                if (newMode === 'viewport' && user) {
                  updatePresence(user._id, { presentationStep: -1 });
                }

                toast({
                  title: 'Sync Mode Changed',
                  description: newMode === 'step'
                    ? '✓ Step Sync: Wall navigates simultaneously'
                    : '○ Viewport Follow: Wall follows viewport (default)',
                  status: 'info',
                  duration: 2000,
                  isClosable: true,
                  position: 'top',
                });
              }}
            />
          </Tooltip>
        </FormControl>

        {/* Match View Toggle */}
        <FormControl display="flex" alignItems="center" justifyContent="center">
          <FormLabel htmlFor="match-view" mb="0" fontSize="sm" mr={2}>
            Match View
          </FormLabel>
          <Tooltip
            label={
              matchView
                ? 'ON: Wall shows same area as laptop'
                : 'OFF: Wall shows more context (default)'
            }
            placement="top"
            hasArrow
          >
            <Switch
              id="match-view"
              size="sm"
              colorScheme="teal"
              isChecked={matchView}
              onChange={(e) => {
                const newValue = e.target.checked;
                setMatchView(newValue);

                // Broadcast to wall clients
                if (user) {
                  updatePresence(user._id, { matchView: newValue });
                }

                toast({
                  title: 'Match View Changed',
                  description: newValue
                    ? '✓ Match View: Wall shows same area as laptop'
                    : '○ Context View: Wall shows more context (default)',
                  status: 'info',
                  duration: 2000,
                  isClosable: true,
                  position: 'top',
                });
              }}
            />
          </Tooltip>
        </FormControl>

        {/* Detour indicator (Phase 2B) */}
        {isInDetour && mainPath && (
          <HStack spacing={2} width="100%">
            <Badge colorScheme="orange" fontSize="sm" px={3} py={1}>
              📍 Q&A Mode
            </Badge>
            <Text fontSize="xs" color={useColorModeValue('gray.600', 'gray.400')}>
              Main path: Step {mainPath.currentStep + 1}
            </Text>
          </HStack>
        )}

        {/* Progress indicator */}
        <HStack spacing={2}>
          <Badge colorScheme="teal" fontSize="md" px={3} py={1}>
            Step {currentStep + 1} of {narrativePath.length}
          </Badge>
          {currentApp && (
            <Text fontSize="sm" fontWeight="medium" maxWidth="300px" noOfLines={1}>
              {currentApp.data.title}
            </Text>
          )}
        </HStack>

        {/* Anchored apps indicator (Phase 4B) */}
        {apps.filter((a) => a.data.anchor === true).length > 0 && (
          <Tooltip
            label="These apps will appear in the left sidebar during presentation (for both you and the wall)"
            placement="top"
            hasArrow
          >
            <Badge colorScheme="green" fontSize="xs" px={2} py={1}>
              📌 {apps.filter((a) => a.data.anchor === true).length} anchored
            </Badge>
          </Tooltip>
        )}

        {/* Navigation controls */}
        <HStack spacing={2}>
          {/* Resume button (Phase 2B) - only show during detour */}
          {canResume && (
            <Tooltip label="Resume Main Path (R key)" placement="top" hasArrow>
              <Button
                leftIcon={<Text fontSize="lg">↩</Text>}
                size="lg"
                colorScheme="orange"
                onClick={handleResume}
              >
                Resume
              </Button>
            </Tooltip>
          )}

          <Tooltip label="Previous (← Arrow)" placement="top" hasArrow>
            <IconButton
              aria-label="Previous step"
              icon={<MdNavigateBefore size={24} />}
              size="lg"
              colorScheme="teal"
              variant="outline"
              onClick={handlePrevious}
              isDisabled={isFirstStep}
            />
          </Tooltip>

          <Tooltip label="Next (→ Arrow or Space)" placement="top" hasArrow>
            <IconButton
              aria-label="Next step"
              icon={<MdNavigateNext size={24} />}
              size="lg"
              colorScheme="teal"
              onClick={handleNext}
              isDisabled={isLastStep}
            />
          </Tooltip>

          <Tooltip label="Exit (Esc)" placement="top" hasArrow>
            <IconButton
              aria-label="Exit presentation"
              icon={<MdClose size={20} />}
              size="lg"
              colorScheme="red"
              variant="outline"
              onClick={handleExitPresentation}
            />
          </Tooltip>
        </HStack>
      </VStack>
    </Box>
  );
}
